import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import { StepProps } from '../../../pages/complete-profile';
import MultiSelect from '../../BaseComponents/MultiSelect';
import {
  GoalsOption,
  HealthConcernOption
} from '../../../types/apiGeneratedTypes';
import { useFormikContext } from 'formik';
import {
  isScanOrPlusMembership,
  OnboardingFormValues,
  shouldShowFemaleSexHormonesStep
} from '../../../types/membersTypes';
import TextInput from '../../BaseComponents/TextInput';
import DatePickerComp from '../../BaseComponents/DatePicker';
import TextArea from '../../BaseComponents/TextArea';

type Step4Props = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  lastCompletedStep?: number;
  isLoading?: boolean;
  isFormError?: boolean;
  membershipType?: string | null;
};

const goalsOptions = [
  {
    key: GoalsOption.IWantAComprehensiveHealthCheck,
    value: GoalsOption.IWantAComprehensiveHealthCheck
  },
  {
    key: GoalsOption.ImFeelingUnwellAndWantClarity,
    value: GoalsOption.ImFeelingUnwellAndWantClarity
  },
  {
    key: GoalsOption.IWantToTakeControlOfMyHealth,
    value: GoalsOption.IWantToTakeControlOfMyHealth
  },
  {
    key: GoalsOption.ImInterestedInPreventativeHealthAndLongevity,
    value: GoalsOption.ImInterestedInPreventativeHealthAndLongevity
  },
  {
    key: GoalsOption.MyDoctorCoachRecommendedIt,
    value: GoalsOption.MyDoctorCoachRecommendedIt
  },
  { key: GoalsOption.Other, value: GoalsOption.Other }
];

const healthConcernsOptions = [
  {
    key: HealthConcernOption.FamilyHistoryGeneticRisk,
    value: HealthConcernOption.FamilyHistoryGeneticRisk
  },
  {
    key: HealthConcernOption.HormonalHealth,
    value: HealthConcernOption.HormonalHealth
  },
  {
    key: HealthConcernOption.FertilityPlanning,
    value: HealthConcernOption.FertilityPlanning
  },
  {
    key: HealthConcernOption.DealingWithAnExistingCondition,
    value: HealthConcernOption.DealingWithAnExistingCondition
  },
  {
    key: HealthConcernOption.InterestedInLongevityBiohacking,
    value: HealthConcernOption.InterestedInLongevityBiohacking
  },
  {
    key: HealthConcernOption.MetabolicHealth,
    value: HealthConcernOption.MetabolicHealth
  },
  { key: HealthConcernOption.Other, value: HealthConcernOption.Other },
  { key: HealthConcernOption.None, value: HealthConcernOption.None }
];

const NONE_KEY = HealthConcernOption.None;

const Step4: React.FC<Step4Props> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData,
  membershipType
}) => {
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<OnboardingFormValues>();
  const isScanOrPlus = isScanOrPlusMembership(membershipType);

  const [hasPopulated, setHasPopulated] = React.useState(false);

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  const onGoalChange = (option: any) => {
    setFieldValue(
      'goals',
      values.goals.some((item) => item.key === option.key)
        ? values.goals.filter((item) => item.key !== option.key)
        : [...values.goals, option]
    );
  };

  const onHealthConcernChange = (option: any) => {
    const current = values.health_concerns || [];
    const isSelected = current.some((item) => item.key === option.key);
    let updated;

    if (option.key === NONE_KEY) {
      updated = isSelected ? [] : [option];
    } else {
      const withoutNone = current.filter((item) => item.key !== NONE_KEY);
      updated = isSelected
        ? withoutNone.filter((item) => item.key !== option.key)
        : [...withoutNone, option];
    }

    setFieldValue('health_concerns', updated);
    setFieldError('health_concerns', undefined);

    if (!updated.length || updated.every((item) => item.key === NONE_KEY)) {
      setFieldValue('health_concern_details', '');
      setFieldError('health_concern_details', undefined);
    }
  };

  const hasConcernDetailsRequired = (values.health_concerns || []).some(
    (item) => item.key !== NONE_KEY
  );

  const validateStepFields = () => {
    const validationErrors: Record<string, string> = {};

    if (!values.health_concerns?.length) {
      validationErrors.health_concerns = 'Please select at least one option';
      setFieldError('health_concerns', 'Please select at least one option');
    } else if (hasConcernDetailsRequired && !values.health_concern_details?.trim()) {
      validationErrors.health_concern_details = 'Please provide more details';
      setFieldError('health_concern_details', 'Please provide more details');
    }

    return validationErrors;
  };

  const handleNextClick = async () => {
    const validationErrors = validateStepFields();
    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const isFemale = values.gender?.key === 'female';
    const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);

    try {
      if (saveStepData) {
        let stepNumber = isScanOrPlus ? 5 : 4;
        if (hasHormonesStep) stepNumber += 1;
        await saveStepData(stepNumber);
      }
    } catch {
      return;
    }

    // Goals → Declaration (final step for all memberships)
    let declarationStep = isScanOrPlus ? 6 : 5;
    if (hasHormonesStep) declarationStep += 1;
    setCurrentStep({ number: declarationStep, name: 'Declaration' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-4'>
      <div className='dropdownWrapper1'>
        <MultiSelect
          title='What brings you to CoreVitals today?'
          name='goals'
          placeholder=''
          options={goalsOptions}
          selectedOptions={values.goals}
          onChange={onGoalChange}
          isError={false}
          errorMessage={''}
        />
      </div>
      <div className='dropdownWrapper'>
        <MultiSelect
          title="Do you have any health concerns you'd like to explore further? *"
          name='health_concerns'
          placeholder=''
          options={healthConcernsOptions}
          selectedOptions={values.health_concerns}
          onChange={onHealthConcernChange}
          isError={!!errors.health_concerns}
          errorMessage={errors.health_concerns as unknown as string}
        />
      </div>
      {errors.health_concerns && (
        <div className='error-message'>{errors.health_concerns as unknown as string}</div>
      )}
      {hasConcernDetailsRequired && (
        <TextArea
          title='If you selected any of the above, please give details: *'
          name='health_concern_details'
          placeholder='Enter details'
          rows={3}
          isError={!!errors.health_concern_details}
          onChange={(e) => {
            setFieldValue('health_concern_details', e.target.value);
            setFieldError('health_concern_details', undefined);
          }}
          value={values.health_concern_details}
        />
      )}
      {errors.health_concern_details && (
        <div className='error-message'>{errors.health_concern_details as string}</div>
      )}
      <DatePickerComp
        title='When did you last have a blood test?'
        placeholder='Enter your blood test date'
        onDateChange={(date) => setFieldValue('last_blood_test', date)}
        selectedDate={values.last_blood_test}
        showTimeInput={false}
        subMaxDateDays={0}
      />
      <TextInput
        title="Is there anything specific you'd like our clinical team to know before your assessment?"
        name='additional_info'
        type='text'
        isError={false}
        onChange={(e) => setFieldValue('additional_info', e.target.value)}
        value={values.additional_info}
      />

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => {
            const isFemale = values.gender?.key === 'female';
            const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);

            if (isScanOrPlus) {
              setCurrentStep({
                number: hasHormonesStep ? 5 : 4,
                name: 'MRI Scan Safety and Clinical Considerations'
              });
            } else {
              setCurrentStep({ number: hasHormonesStep ? 4 : 3, name: 'Medical History' });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button text='Next' onClick={handleNextClick} />
      </div>
    </div>
  );
};
export default Step4;
